import type { CallSessionDto } from '@therame/shared';

/**
 * A TAN tab (#1856): the call token a TAN was exchanged for, kept in
 * `sessionStorage` so that only this tab is a TAN session. The Keycloak session
 * and every other tab stay as they are; keycloak-js is never initialised here.
 */
const CALL_SESSION_KEY = 'call_session';

export type TabCallSession = Pick<CallSessionDto, 'token' | 'callId' | 'patientId' | 'expiresAt'>;

export const readCallSession = (): TabCallSession | null => {
  const raw = window.sessionStorage.getItem(CALL_SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Partial<TabCallSession>;
    if (!session.token || !session.callId || !session.patientId) return null;
    return session as TabCallSession;
  } catch {
    return null;
  }
};

export const storeCallSession = (session: CallSessionDto) => {
  const stored: TabCallSession = {
    token: session.token,
    callId: session.callId,
    patientId: session.patientId,
    expiresAt: session.expiresAt,
  };
  window.sessionStorage.setItem(CALL_SESSION_KEY, JSON.stringify(stored));
};

export const clearCallSession = () => {
  window.sessionStorage.removeItem(CALL_SESSION_KEY);
};

/** Whether this tab runs as a TAN session. */
export const isCallSessionTab = (): boolean => readCallSession() !== null;

/** The token to send as `X-Therame-Call-Session`, or null outside a TAN tab. */
export const getCallSessionToken = (): string | null => readCallSession()?.token ?? null;

/** The page a TAN link opens; it starts every tab afresh, without a session. */
export const TAN_LOGIN_PATH = '/tan-login';

/**
 * Ends the TAN session of this tab: the token goes, the tab starts over at the
 * TAN page. Nothing of Keycloak is touched, so other tabs stay signed in.
 */
export const endCallSession = () => {
  clearCallSession();
  window.location.replace(TAN_LOGIN_PATH);
};
